import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo } from 'react';
import { useSuspenseManagedLighthouses } from '#api/managed-lighthouses.queries.ts';
import { StatusDot } from '#components/atoms/icons/StatusDot.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { IpAddressPill } from '#components/atoms/pill/ip-address-pill/IpAddressPill.tsx';
import { IpAddressPillList } from '#components/patterns/ip-address-pill-list/IpAddressPillList.tsx';
import { NameCell } from '#components/patterns/table/name-cell/NameCell.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { Network } from '#dn-types/network.ts';
import { getCidrV4 } from '#util/networkCidrs.ts';
import { NetworkActionMenu } from './action-menu/NetworkActionMenu.tsx';
import styles from './NetworksList.module.css';

type Props = {
  data: Network[];
  isFetching: boolean;
};

const columnHelper = createColumnHelper<Network>();

const numFormatter = new Intl.NumberFormat();

export function NetworksList({ data, isFetching }: Props) {
  // One org-wide fetch, joined to the rows by networkID (tenancies are 1:1 with networks).
  const managedLighthouses = useSuspenseManagedLighthouses();
  const managedLighthousesByNetworkID = useMemo(
    () => new Map(managedLighthouses.map((lighthouse) => [lighthouse.networkID, lighthouse])),
    [managedLighthouses]
  );
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Network',
        size: 270,
        cell: (info) => {
          const name = info.getValue();
          const { id, description, hostCount } = info.row.original;
          return (
            <NameCell
              name={name}
              nameTo={id}
              secondRow={
                <span className="flex">
                  <Link className="a nowrap" to={`/hosts?filter.networkID=${id}`}>
                    {numFormatter.format(hostCount)} {hostCount === 1 ? 'host' : 'hosts'}
                  </Link>
                  {description ?
                    <>
                      <span className="nowrap">&nbsp;/&nbsp;</span>
                      <span className="truncate">{description}</span>
                    </>
                  : null}
                </span>
              }
            />
          );
        },
        meta: {
          truncate: true,
        },
      }),
      columnHelper.accessor('cidrs', {
        header: 'CIDR range',
        size: 180,
        cell: (info) => <IpAddressPillList ipAddresses={info.getValue()} />,
        meta: {
          className: styles.NetworksList_cidr,
        },
      }),
      columnHelper.accessor('ipv4AddrCount', {
        header: 'IPv4 allocation',
        size: 150,
        cell: (info) => {
          const ipv4AddrCount = info.getValue();
          const cidrV4 = getCidrV4(info.row.original);
          if (!cidrV4) {
            return '—';
          }
          const prefixLength = Number.parseInt(cidrV4.split('/')[1] ?? '', 10);
          if (!Number.isNaN(prefixLength) && prefixLength >= 1 && prefixLength <= 30) {
            const maxUsable = 2 ** (32 - prefixLength) - 2;
            return `${numFormatter.format(ipv4AddrCount)} of ${numFormatter.format(maxUsable)}`;
          }
          return ipv4AddrCount;
        },
        meta: {
          align: 'center',
          className: styles.NetworksList_ipv4AddrCount,
        },
      }),
      columnHelper.accessor((row) => managedLighthousesByNetworkID.get(row.id), {
        id: 'managedLighthouse',
        header: 'Managed lighthouse',
        size: 180,
        cell: (info) => {
          const managedLighthouse = info.getValue();
          if (info.row.original.disableManagedLighthouses) {
            return (
              <span className="flex align-center">
                <StatusDot className="shrink-0 mr-8" status="off" decorative={true} />
                <span>Off</span>
              </span>
            );
          }
          // No entry means no tenancy at all (v1 networks are IPv4-only and never get one) —
          // opted-in v2 networks always have an entry, pending or active.
          if (!managedLighthouse) {
            return '—';
          }
          const ipAddress = managedLighthouse.ipAddresses[0];
          if (managedLighthouse.status === 'pending') {
            return (
              <span className="flex align-center">
                <StatusDot className="shrink-0 mr-8" status="pending" decorative={true} />
                <span>Pending</span>
              </span>
            );
          }
          return (
            <span className="flex align-center">
              <StatusDot className="shrink-0 mr-8" status="active" />
              {/* -ml-4 cancels the pill's 4px padding so the address text starts at the same x
                  as the Pending/Off labels in sibling rows */}
              {ipAddress ?
                <IpAddressPill className="-ml-4" ipAddress={ipAddress} />
              : '—'}
            </span>
          );
        },
      }),
      columnHelper.accessor('curve', {
        header: 'Curve',
        size: 160,
        meta: {
          align: 'center',
          className: styles.NetworksList_curve,
        },
      }),
      columnHelper.display({
        id: 'action-menu',
        header: '',
        size: 34,
        cell(info) {
          const { id, certVersion } = info.row.original;
          return (
            <div className="flex justify-end">
              <NetworkActionMenu networkId={id} certVersion={certVersion} />
            </div>
          );
        },
      }),
    ],
    [managedLighthousesByNetworkID]
  );

  return <Table data={data} columns={columns} className={cn({ 'opacity-dim': isFetching })} rowHeight="tall" />;
}
